import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicPetProfile } from '../services/petApi';
import { getPublicPetPhotos, publicThumbUrl, publicPhotoUrl } from '../services/petMediaApi';
import config from '../config/appConfig';
import '../styles/PublicProfile.css';

const SPECIES_ICONS = {
  dog:'🐕', cat:'🐈', bird:'🐦', rabbit:'🐇',
  reptile:'🦎', fish:'🐠', small_animal:'🐹', horse:'🐴', other:'🐾'
};

const Section = ({ title, children }) => (
  <div className="info-section">
    <h3>{title}</h3>
    {children}
  </div>
);

const Row = ({ label, value }) =>
  value ? (
    <div className="info-row">
      <span className="info-label">{label}</span>
      <span className="info-value">{value}</span>
    </div>
  ) : null;

export default function PetPublicProfile() {
  const { qrCodeId } = useParams();
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    getPublicPetProfile(qrCodeId)
      .then(res => {
        const p = res.data.profile;
        setProfile(p);
        // Load public photos using the petId returned in the profile
        if (p.petId) {
          getPublicPetPhotos(p.petId)
            .then(r => setPhotos(r.data.photos))
            .catch(() => {}); // photos are optional
        }
      })
      .catch(err => setError(err.response?.data?.message || 'Profile not found'))
      .finally(() => setLoading(false));
  }, [qrCodeId]);

  if (loading) return (
    <div className="public-profile-container">
      <div className="loading">Loading profile…</div>
    </div>
  );

  if (error) return (
    <div className="public-profile-container">
      <div className="error-message">
        <h2>🐾 Profile Unavailable</h2>
        <p>{error}</p>
        <p style={{ marginTop: '1rem', fontSize: '.85rem', color: '#718096' }}>
          This QR code may not be registered yet, or the owner has disabled public access.
        </p>
      </div>
    </div>
  );

  if (!profile) return null;

  const speciesIcon = SPECIES_ICONS[profile.species] ?? '🐾';
  const capitalise  = s => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';

  return (
    <div className="public-profile-container">
      <div className="public-profile-card">

        {/* Header */}
        <div className="profile-header" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '.5rem' }}>{speciesIcon}</div>
          <h1>{profile.petName}</h1>
          {profile.species && (
            <p style={{ color: '#718096', textTransform: 'capitalize', margin: '0 0 .5rem' }}>
              {capitalise(profile.species)}{profile.breed ? ` · ${profile.breed}` : ''}
            </p>
          )}
          <div className="profile-badge" style={{ margin: '0 auto' }}>
            {config.appIcon} {config.appName}
          </div>
        </div>

        {/* Custom message */}
        {profile.message && (
          <div className="emergency-message">
            <strong>📋 Important:</strong> {profile.message}
          </div>
        )}

        {/* Photo gallery */}
        {photos.length > 0 && (
          <Section title="📷 Photos">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
              {photos.map(p => (
                <img
                  key={p._id}
                  src={publicThumbUrl(p.thumbnailFilename)}
                  alt={p.caption || profile.petName}
                  style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1.5px solid #e2e8f0' }}
                  onClick={() => setLightbox(p)}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Medical info */}
        {(profile.bloodType || profile.allergies?.length || profile.conditions?.length ||
          profile.vaccinations?.length || profile.medications?.length) && (
          <Section title="🏥 Medical Information">
            <Row label="Blood Type" value={profile.bloodType} />
            {profile.allergies?.length > 0 && (
              <div className="info-row">
                <span className="info-label">Allergies</span>
                <span className="info-value">{profile.allergies.join(', ')}</span>
              </div>
            )}
            {profile.conditions?.length > 0 && (
              <div className="info-row">
                <span className="info-label">Conditions</span>
                <span className="info-value">{profile.conditions.join(', ')}</span>
              </div>
            )}
            {profile.medications?.length > 0 && (
              <div className="info-row">
                <span className="info-label">Medications</span>
                <ul className="info-list">
                  {profile.medications.map((m, i) => (
                    <li key={i}>{m.name}{m.dose ? ` — ${m.dose}` : ''}{m.frequency ? ` (${m.frequency})` : ''}</li>
                  ))}
                </ul>
              </div>
            )}
          </Section>
        )}

        {/* Vaccinations */}
        {profile.vaccinations?.length > 0 && (
          <Section title="💉 Vaccinations">
            {profile.vaccinations.map((v, i) => (
              <div key={i} className="info-row">
                <span className="info-label">{v.name}</span>
                <span className="info-value">
                  {v.date ? new Date(v.date).toLocaleDateString() : '—'}
                  {v.nextDue ? ` · Next: ${new Date(v.nextDue).toLocaleDateString()}` : ''}
                </span>
              </div>
            ))}
          </Section>
        )}

        {/* Vet */}
        {profile.vet && (profile.vet.name || profile.vet.clinic) && (
          <Section title="🩺 Veterinarian">
            <Row label="Name"   value={profile.vet.name} />
            <Row label="Clinic" value={profile.vet.clinic} />
            <Row label="Phone"  value={profile.vet.phone} />
          </Section>
        )}

        {/* Owner contact */}
        {(profile.ownerName || profile.ownerEmail) && (
          <Section title="👤 Owner">
            <Row label="Name"  value={profile.ownerName} />
            <Row label="Email" value={profile.ownerEmail} />
          </Section>
        )}

        {/* Footer */}
        <div className="profile-footer">
          <p>Profile powered by {config.appName}</p>
          <p style={{ fontSize: '.75rem', color: '#a0aec0', marginTop: '.25rem' }}>
            ID: {qrCodeId}
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
          onClick={() => setLightbox(null)}
        >
          <div style={{ position: 'relative', maxWidth: 700, width: '100%' }}>
            <img
              src={publicPhotoUrl(lightbox.filename)}
              alt={lightbox.caption || profile.petName}
              style={{ width: '100%', borderRadius: 12, maxHeight: '80vh', objectFit: 'contain' }}
            />
            {lightbox.caption && (
              <p style={{ color: '#fff', textAlign: 'center', marginTop: 10, fontSize: '.9rem' }}>
                {lightbox.caption}
              </p>
            )}
            <button
              style={{ position: 'absolute', top: -12, right: -12, background: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}
              onClick={() => setLightbox(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
